import { useAuth0 } from '@auth0/auth0-react';
import { GraphQLClient, type RequestOptions, type Variables } from 'graphql-request';
import { useMemo, useRef } from 'react';

// Auth0 errors whose recovery is "send the user through Universal Login":
// expired/revoked refresh tokens, cleared storage, consent revocation, etc.
export const REDIRECTABLE_ERRORS: ReadonlySet<string> = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
  'invalid_grant',
]);

// Thrown by `useAuthedClient.request()` when the token fetch failed with a
// redirectable Auth0 code and `loginWithRedirect()` has been kicked off.
// The query/mutation layer recognises this tag and skips error UI so the
// user doesn't see a failure flash before Universal Login navigation lands.
export class AuthRedirectError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = 'AuthRedirectError';
    this.code = code;
  }
}

export interface AuthedClient {
  request<T = unknown, V extends Variables = Variables>(
    options: RequestOptions<V, T>,
  ): Promise<T>;
}

export function useAuthedClient(): AuthedClient {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // Refs keep the returned object stable across auth0 callback-identity
  // churn, so consuming hooks (#181) don't suffer re-fetch storms when
  // the SDK re-creates its callbacks on internal state transitions.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  const redirectRef = useRef(loginWithRedirect);
  redirectRef.current = loginWithRedirect;

  return useMemo<AuthedClient>(() => {
    // Defer baseUrl/endpoint computation until first use so module import
    // doesn't require `window` (allows shared imports from non-DOM tools).
    const baseUrl = (
      import.meta.env.VITE_API_BASE_URL ?? window.location.origin
    ).replace(/\/+$/, '');
    const endpoint = `${baseUrl}/graphql`;
    const client = new GraphQLClient(endpoint);

    return {
      request: async <T = unknown, V extends Variables = Variables>(
        options: RequestOptions<V, T>,
      ): Promise<T> => {
        let token: string;
        try {
          token = await getTokenRef.current();
        } catch (err) {
          const code = (err as { error?: unknown } | null)?.error;
          if (typeof code === 'string' && REDIRECTABLE_ERRORS.has(code)) {
            // Await the redirect so we know whether the browser is actually
            // navigating. If the SDK rejects (popup blocked, CSP, network),
            // fall through to the original error — otherwise the hook layer
            // would swallow `AuthRedirectError` and pin the UI to a spinner
            // forever with no recovery signal.
            try {
              await redirectRef.current();
            } catch (redirectErr) {
              console.error('Auth0 loginWithRedirect failed', redirectErr);
              throw err;
            }
            // Tag the throw so the query/mutation layer can skip error UI
            // for the brief window before Universal Login navigates away.
            throw new AuthRedirectError(code);
          }
          throw err;
        }
        // Per-request headers so concurrent callers cannot interleave
        // setHeader/request and dispatch with each other's tokens.
        const requestHeaders = new Headers(options.requestHeaders);
        requestHeaders.set('Authorization', `Bearer ${token}`);
        return client.request<T, V>({ ...options, requestHeaders });
      },
    };
  }, []);
}
